/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  ViewEncapsulation,
  AfterContentChecked,
  OnInit,
  Input,
  ContentChildren,
  QueryList,
  Renderer2,
  ElementRef,
  Optional,
} from '@angular/core';
import {MdGridTile} from './grid-tile';
import {TileCoordinator} from './tile-coordinator';
import {TileStyler, FitTileStyler, RatioTileStyler, FixedTileStyler} from './tile-styler';
import {Dir} from '../core';
import {
  coerceToString,
  coerceToNumber,
} from './grid-list-measure';


// TODO(kara): Conditional (responsive) column count / row size.
// TODO(kara): Re-layout on window resize / media change (debounced).
// TODO(kara): gridTileHeader and gridTileFooter.

const /** @type {?} */ MD_FIT_MODE = 'fit';
export class MdGridList implements OnInit, AfterContentChecked {
/**
 * Number of columns being rendered.
 */
private _cols: number;
/**
 * Row height value passed in by user. This can be one of three types:
 * - Number value (ex: "100px"):  sets a fixed row height to that value
 * - Ratio value (ex: "4:3"): sets the row height based on width:height ratio
 * - "Fit" mode (ex: "fit"): sets the row height to total height divided by number of rows
 */
private _rowHeight: string;
/**
 * The amount of space between tiles. This will be something like '5px' or '2em'.
 */
private _gutter: string = '1px';
/**
 * Sets position and size styles for a tile
 */
private _tileStyler: TileStyler;
/**
 * Query list of tiles that are being rendered.
 */
_tiles: QueryList<MdGridTile>;
/**
 * @param {?} _renderer
 * @param {?} _element
 * @param {?} _dir
 */
constructor(
private _renderer: Renderer2,
private _element: ElementRef,
private _dir: Dir) {}
/**
 * Amount of columns in the grid list.
 * @return {?}
 */
get cols() { return this._cols; }
/**
 * @param {?} value
 * @return {?}
 */
set cols(value: any) { this._cols = coerceToNumber(value); }
/**
 * Size of the grid list's gutter in pixels.
 * @return {?}
 */
get gutterSize() { return this._gutter; }
/**
 * @param {?} value
 * @return {?}
 */
set gutterSize(value: any) { this._gutter = coerceToString(value); }
/**
 * Set internal representation of row height from the user-provided value.
 * @param {?} value
 * @return {?}
 */
set rowHeight(value: string | number) {
    this._rowHeight = coerceToString(value);
    this._setTileStyler();
  }
/**
 * @return {?}
 */
ngOnInit() {
    this._checkCols();
    this._checkRowHeight();
  }
/**
 * The layout calculation is fairly cheap if nothing changes, so there's little cost
 * to run it frequently.
 * @return {?}
 */
ngAfterContentChecked() {
    this._layoutTiles();
  }
/**
 * Throw a friendly error if cols property is missing
 * @return {?}
 */
private _checkCols() {
    if (!this.cols) {
      throw Error(`md-grid-list: must pass in number of columns. ` +
                      `Example: <md-grid-list cols="3">`);
    }
  }
/**
 * Default to equal width:height if rowHeight property is missing
 * @return {?}
 */
private _checkRowHeight(): void {
    if (!this._rowHeight) {
      this._tileStyler = new RatioTileStyler('1:1');
    }
  }
/**
 * Creates correct Tile Styler subtype based on rowHeight passed in by user
 * @return {?}
 */
private _setTileStyler(): void {
    if (this._rowHeight === MD_FIT_MODE) {
      this._tileStyler = new FitTileStyler();
    } else if (this._rowHeight && this._rowHeight.indexOf(':') > -1) {
      this._tileStyler = new RatioTileStyler(this._rowHeight);
    } else {
      this._tileStyler = new FixedTileStyler(this._rowHeight);
    }
  }
/**
 * Computes and applies the size and position for all children grid tiles.
 * @return {?}
 */
private _layoutTiles(): void {
    let /** @type {?} */ tracker = new TileCoordinator(this.cols, this._tiles);
    let /** @type {?} */ direction = this._dir ? this._dir.value : 'ltr';
    this._tileStyler.init(this.gutterSize, tracker, this.cols, direction);

    this._tiles.forEach((tile, index) => {
      let /** @type {?} */ pos = tracker.positions[index];
      this._tileStyler.setStyle(tile, pos.row, pos.col);
    });

    this._setListStyle(this._tileStyler.getComputedHeight());
  }
/**
 * Sets style on the main grid-list element, given the style name and value.
 * @param {?} style
 * @return {?}
 */
_setListStyle(style: [string, string]): void {
    if (style) {
      this._renderer.setStyle(this._element.nativeElement, style[0], style[1]);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-grid-list, mat-grid-list',
  templateUrl: 'grid-list.html',
  styleUrls: ['grid-list.css'],
  host: {
    'role': 'list',
    'class': 'mat-grid-list',
  },
  encapsulation: ViewEncapsulation.None,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_tiles': [{ type: ContentChildren, args: [MdGridTile, ] },],
'cols': [{ type: Input },],
'gutterSize': [{ type: Input },],
'rowHeight': [{ type: Input },],
};
}

function MdGridList_tsickle_Closure_declarations() {
/** @type {?} */
MdGridList.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdGridList.ctorParameters;
/** @type {?} */
MdGridList.propDecorators;
/**
 * Number of columns being rendered.
 * @type {?}
 */
MdGridList.prototype._cols;
/**
 * Row height value passed in by user. This can be one of three types:
 * - Number value (ex: "100px"):  sets a fixed row height to that value
 * - Ratio value (ex: "4:3"): sets the row height based on width:height ratio
 * - "Fit" mode (ex: "fit"): sets the row height to total height divided by number of rows
 * @type {?}
 */
MdGridList.prototype._rowHeight;
/**
 * The amount of space between tiles. This will be something like '5px' or '2em'.
 * @type {?}
 */
MdGridList.prototype._gutter;
/**
 * Sets position and size styles for a tile
 * @type {?}
 */
MdGridList.prototype._tileStyler;
/**
 * Query list of tiles that are being rendered.
 * @type {?}
 */
MdGridList.prototype._tiles;
/** @type {?} */
MdGridList.prototype._renderer;
/** @type {?} */
MdGridList.prototype._element;
/** @type {?} */
MdGridList.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
